import axios from 'axios';
import { supabase } from '../auth/SupabaseClient';

type ApiFetchOptions = RequestInit & {
  auth?: boolean;
};

const configuredBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL;
const API_BASES = configuredBase
  ? [configuredBase]
  : ['http://localhost:8000', 'http://127.0.0.1:8000', 'http://0.0.0.0:8000'];
const LEGACY_LOCAL_API_BASES = ['http://localhost:8000', 'http://127.0.0.1:8000', 'http://0.0.0.0:8000'];

const API_KEY = process.env.REACT_APP_API_KEY || '';
const SHOULD_LOG_DEBUG = process.env.NODE_ENV === 'development';

let activeApiBase = API_BASES[0];
let apiClientInterceptorsInstalled = false;

const devLog = (...args: unknown[]) => {
  if (SHOULD_LOG_DEBUG) {
    console.log(...args);
  }
};

const devWarn = (...args: unknown[]) => {
  if (SHOULD_LOG_DEBUG) {
    console.warn(...args);
  }
};

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

const normalizeEndpoint = (endpoint: string) => endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

const headersToRecord = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return Object.entries(headers as Record<string, string>).reduce<Record<string, string>>(
    (record, [key, value]) => {
      if (typeof value === 'string') {
        record[key] = value;
      }
      return record;
    },
    {}
  );
};

export const getActiveApiBase = () => activeApiBase;

export const buildApiUrl = (endpoint: string, base = activeApiBase) => {
  if (isAbsoluteUrl(endpoint)) {
    return endpoint;
  }
  return `${base}${normalizeEndpoint(endpoint)}`;
};

export const buildWebSocketUrl = (endpoint: string, base = activeApiBase) => {
  const apiUrl = new URL(buildApiUrl(endpoint, base));
  apiUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  return apiUrl.toString();
};

const rewriteApiUrl = (url: string) => {
  const matchedBase = LEGACY_LOCAL_API_BASES.find((base) => url.startsWith(base));
  if (matchedBase) {
    return `${activeApiBase}${url.slice(matchedBase.length)}`;
  }

  if (url.startsWith('/api/') || url === '/health') {
    return `${activeApiBase}${url}`;
  }

  if (typeof window !== 'undefined' && isAbsoluteUrl(url)) {
    try {
      const parsed = new URL(url);
      if (
        parsed.origin === window.location.origin &&
        (parsed.pathname.startsWith('/api/') || parsed.pathname === '/health')
      ) {
        return `${activeApiBase}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return url;
    }
  }

  return url;
};

const isApiRequestUrl = (url: string) => {
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : activeApiBase);
    return parsed.pathname.startsWith('/api/');
  } catch {
    return url.startsWith('/api/');
  }
};

export const installApiFetchInterceptor = () => {
  if (apiClientInterceptorsInstalled || typeof window === 'undefined') {
    return;
  }
  apiClientInterceptorsInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let rewrittenInput = input;
    let urlForAuth = '';

    if (typeof input === 'string') {
      rewrittenInput = rewriteApiUrl(input);
      urlForAuth = rewrittenInput;
    } else if (input instanceof URL) {
      const rewritten = rewriteApiUrl(input.toString());
      rewrittenInput = rewritten === input.toString() ? input : rewritten;
      urlForAuth = rewritten;
    } else if (typeof Request !== 'undefined' && input instanceof Request) {
      const rewritten = rewriteApiUrl(input.url);
      if (rewritten !== input.url) {
        rewrittenInput = new Request(rewritten, input);
      }
      urlForAuth = rewritten;
    }

    if (!urlForAuth || !isApiRequestUrl(urlForAuth)) {
      return originalFetch(rewrittenInput, init);
    }

    const authHeaders = await getAuthHeaders();
    const requestHeaders = headersToRecord(init?.headers);
    return originalFetch(rewrittenInput, {
      ...init,
      headers: {
        ...authHeaders,
        ...requestHeaders,
      },
    });
  };

  axios.interceptors.request.use(async (config) => {
    if (!config.url) {
      return config;
    }

    const rewrittenUrl = rewriteApiUrl(config.url);
    if (rewrittenUrl !== config.url) {
      config.url = rewrittenUrl;
      config.baseURL = undefined;
    }

    if (isApiRequestUrl(rewrittenUrl)) {
      const authHeaders = await getAuthHeaders();
      config.headers = {
        ...(config.headers || {}),
        ...authHeaders,
      } as typeof config.headers;
    }

    return config;
  });
};

export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};
  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }

  if (!supabase) {
    return headers;
  }

  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // The API key still covers CLI/local flows when Supabase is unavailable.
  }

  return headers;
};

export const apiFetch = async (endpoint: string, options: ApiFetchOptions = {}) => {
  const { auth = true, headers: optionHeaders, ...fetchOptions } = options;
  const authHeaders = auth ? await getAuthHeaders() : {};
  const requestHeaders = headersToRecord(optionHeaders);
  const body = fetchOptions.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const shouldSetJsonContentType = body !== undefined && !isFormData && !requestHeaders['Content-Type'];

  const headers = {
    Accept: 'application/json',
    ...authHeaders,
    ...(shouldSetJsonContentType ? { 'Content-Type': 'application/json' } : {}),
    ...requestHeaders,
  };

  const requestOptions: RequestInit = {
    method: 'GET',
    mode: 'cors',
    cache: 'no-cache',
    ...fetchOptions,
    headers,
  };

  if (isAbsoluteUrl(endpoint)) {
    return fetch(endpoint, requestOptions);
  }

  let lastError: unknown = null;
  for (const baseUrl of API_BASES) {
    const fullUrl = buildApiUrl(endpoint, baseUrl);
    try {
      devLog(`API request: ${fullUrl}`);
      const response = await fetch(fullUrl, requestOptions);
      activeApiBase = baseUrl;
      return response;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      lastError = error;
      devWarn(`API request failed for ${baseUrl}:`, error);
    }
  }

  const message = lastError instanceof Error ? lastError.message : String(lastError || 'Unknown error');
  throw new Error(`All API endpoints failed. Last error: ${message}`);
};

export const apiRequest = async (endpoint: string, options: ApiFetchOptions = {}) => {
  const response = await apiFetch(endpoint, options);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response;
};

export const testApiConnectivity = async () => {
  try {
    const response = await apiRequest('/api/system/health');
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      troubleshooting: [
        'Disable browser extensions that block localhost requests',
        'Check REACT_APP_API_BASE',
        'Confirm the backend is reachable',
      ],
    };
  }
};

export const checkBackendMode = async () => {
  try {
    const response = await apiFetch('/health', { auth: false });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return { mode: data.mode || 'full', healthy: true };
  } catch {
    return null;
  }
};

declare global {
  interface Window {
    apiRequest?: typeof apiRequest;
    testApiConnectivity?: typeof testApiConnectivity;
  }
}

if (typeof window !== 'undefined') {
  window.apiRequest = apiRequest;
  window.testApiConnectivity = testApiConnectivity;
}
